/**
 * The review list "Show recommendations" opens on the Assignments page
 * (#2891): OttoCloud's pick for each agent beside the model it uses now,
 * a tick on each one that differs, and Apply.
 *
 * Its own file because the Assignments page is already long and this is a
 * self-contained panel: it owns its ticks, its Apply loop and each agent's
 * result, and gets everything else from the page. It does not save
 * anything itself. Apply hands each ticked agent to the page's own per-agent
 * Save (#2890), one at a time, so a recommendation is checked and saved
 * exactly as a model picked by hand would be.
 *
 * The list describes the moment it was asked. "Now" is the model each agent
 * used when the button was pressed, not a live value, so an agent that Apply
 * just changed keeps its row and its result instead of turning into
 * "Already the recommended model". Once Apply saves an agent's model, that
 * row alone says so: the old model reads "Before", without its
 * "Not working" badge, and the recommended one reads "Now". A row Apply
 * didn't save keeps its lines as they were.
 */
import { useState } from 'react'
import { Badge, Button } from '@/components/ui'
import { CostCategoryPill } from '@/components/CostCategoryPill'
import { modelCostCategory, AGENT_LABEL } from '@/lib/agentModelSpec'
import type { AdminAgentName, AgentRecommendation, ModelPricingRow } from '@/lib/api'

/** One agent's line, fixed when the list was asked for. */
export interface RecommendationItem {
  agent: AdminAgentName
  /** The model the agent used when the list was asked for. */
  now: string
  /** Whether that model was failing its check, shown beside it. */
  nowNotWorking: boolean
  /** OttoCloud's answer for this agent. */
  rec: AgentRecommendation
  /** True when the recommended model is the one it uses now. */
  alreadyRecommended: boolean
}

/** What the panel shows: waiting, one message instead of a list, or the list. */
export type RecommendationsView =
  | { phase: 'asking' }
  | { phase: 'message'; text: string }
  | { phase: 'ready'; items: readonly RecommendationItem[] }

/** What one agent's Apply found, in the words the list shows. */
export interface ApplyResult {
  tone: 'ok' | 'bad' | 'warn'
  text: string
  /** Whether the model was written. A save whose check couldn't reach the
   * supplier is still a save. */
  saved: boolean
}

type ItemResult = ApplyResult | { tone: 'wait'; text: string }

const RESULT_STYLE: Record<ItemResult['tone'], string> = {
  ok: 'text-success',
  bad: 'text-destructive',
  warn: 'text-warning',
  wait: 'text-muted-foreground',
}

/** Where a pick comes from: OttoCloud's maintained list, or installs that
 * share scores. The second says "shared runs", not "community pick":
 * nobody votes, OttoCloud takes the best grades from the runs installs
 * shared, and the count is the number of those runs it sent. */
function BasisPill({ rec }: { rec: Extract<AgentRecommendation, { status: 'recommended' }> }) {
  return rec.basis === 'community' ? (
    <span className="whitespace-nowrap rounded-md bg-muted px-1.5 py-px text-[10px] font-semibold text-muted-foreground">
      From {rec.runs.toLocaleString()} community shared run{rec.runs === 1 ? '' : 's'}
    </span>
  ) : (
    <span className="whitespace-nowrap rounded-md bg-success/15 px-1.5 py-px text-[10px] font-semibold text-success">
      Lab-tested
    </span>
  )
}

function tickable(item: RecommendationItem): boolean {
  return item.rec.status === 'recommended' && !item.alreadyRecommended
}

export function RecommendationsList({
  view,
  pricingRows,
  onApply,
  busy = false,
  onRunningChange,
  onClose,
}: {
  view: RecommendationsView
  pricingRows: readonly ModelPricingRow[]
  /** Checks and saves one agent's recommended model through its Save.
   * Never throws: a failure comes back as a `bad` result. */
  onApply: (agent: AdminAgentName, model: string) => Promise<ApplyResult>
  /** An agent's own Save is running; Apply waits for it. */
  busy?: boolean
  /** Told when Apply starts and stops, so the page can hold its own
   * buttons meanwhile. */
  onRunningChange?: (running: boolean) => void
  onClose: () => void
}) {
  const items = view.phase === 'ready' ? view.items : []
  // Every pick that differs starts ticked, since the list exists to apply
  // them, so only the ones a person unticks are kept. That also holds when
  // the answer arrives after the panel first drew as "Asking OttoCloud…".
  const [unticked, setUnticked] = useState<ReadonlySet<AdminAgentName>>(() => new Set())
  const [results, setResults] = useState<Partial<Record<AdminAgentName, ItemResult>>>({})
  const [running, setRunning] = useState(false)
  const [finished, setFinished] = useState(false)

  const tickedItems = items.filter((i) => tickable(i) && !unticked.has(i.agent) && !results[i.agent])
  const count = tickedItems.length

  function toggle(agent: AdminAgentName): void {
    setUnticked((prev) => {
      const next = new Set(prev)
      if (!next.delete(agent)) next.add(agent)
      return next
    })
  }

  /** One at a time, like the agent's own Save: each is checked before it is
   * saved, and two at once could finish out of order. */
  async function apply(): Promise<void> {
    const batch = tickedItems
    setRunning(true)
    onRunningChange?.(true)
    setFinished(false)
    for (const item of batch) {
      if (item.rec.status !== 'recommended') continue
      setResults((prev) => ({ ...prev, [item.agent]: { tone: 'wait', text: 'Checking…' } }))
      const result = await onApply(item.agent, item.rec.model)
      setResults((prev) => ({ ...prev, [item.agent]: result }))
    }
    setRunning(false)
    onRunningChange?.(false)
    setFinished(true)
  }

  return (
    <div
      className="grid gap-2.5 rounded-lg border bg-primary/5 p-3"
      aria-live="polite"
      data-testid="recommendations"
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <b className="text-[15px]">Recommendations from OttoCloud</b>
        {view.phase === 'ready' && (
          <span className="text-xs text-muted-foreground">
            OttoCloud's lab-tested picks for each agent, from the supplier it uses now. Ticked ones differ from what
            the agent uses today.
          </span>
        )}
      </div>

      {view.phase === 'asking' && <p className="text-sm text-muted-foreground">Asking OttoCloud…</p>}

      {view.phase === 'message' && (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm text-destructive"
          role="alert"
        >
          {view.text}
        </div>
      )}

      {view.phase === 'ready' && (
        <>
          <div className="overflow-hidden rounded-md border bg-card">
            {items.map((item) => {
              const result = results[item.agent]
              // Saved by this Apply: the agent no longer uses `now`.
              const saved = result !== undefined && result.tone !== 'wait' && result.saved
              const rec = item.rec
              const label = AGENT_LABEL[item.agent]
              const canTick = tickable(item)
              const cost = rec.status === 'recommended' ? modelCostCategory(rec.model, pricingRows) : undefined
              const body = (
                <>
                  <span className="text-sm font-semibold">{label}</span>
                  <span className="grid min-w-0 gap-1">
                    <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                      <span className="w-[88px] flex-none text-muted-foreground">{saved ? 'Before' : 'Now'}</span>
                      <span className={`break-all font-mono${saved ? ' text-muted-foreground' : ''}`}>{item.now}</span>
                      {item.nowNotWorking && !saved && (
                        <Badge variant="destructive" className="text-[10px]">
                          Not working
                        </Badge>
                      )}
                      {rec.status === 'recommended' && item.alreadyRecommended && (
                        <span className="text-muted-foreground">Already the recommended model.</span>
                      )}
                      {rec.status === 'none' && (
                        <span className="text-muted-foreground">OttoCloud has no recommendation for this agent.</span>
                      )}
                      {rec.status === 'unavailable' && (
                        <span className="text-muted-foreground">
                          Couldn't get a recommendation for this agent just now.
                        </span>
                      )}
                    </span>
                    {canTick && rec.status === 'recommended' && (
                      <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                        <span className="w-[88px] flex-none text-muted-foreground">{saved ? 'Now' : 'Recommended'}</span>
                        <span className="break-all font-mono font-semibold">{rec.model}</span>
                        {cost ? <CostCategoryPill category={cost} /> : null}
                        <BasisPill rec={rec} />
                      </span>
                    )}
                    {result && (
                      <span
                        className={`text-xs font-medium ${RESULT_STYLE[result.tone]}`}
                        data-testid={`${item.agent}-recommendation-result`}
                      >
                        {result.text}
                      </span>
                    )}
                  </span>
                </>
              )
              const rowClass =
                'grid grid-cols-[24px_minmax(0,1fr)] sm:grid-cols-[28px_minmax(84px,120px)_minmax(0,1fr)] items-start gap-x-2.5 gap-y-1 border-t px-3 py-2.5 first:border-t-0 [&>span:last-child]:col-start-2 sm:[&>span:last-child]:col-start-auto'
              return canTick ? (
                <label key={item.agent} className={`${rowClass} cursor-pointer`} data-testid={`${item.agent}-recommendation`}>
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 accent-primary"
                    checked={!unticked.has(item.agent) && !result}
                    disabled={running || Boolean(result)}
                    onChange={() => toggle(item.agent)}
                    aria-label={`Apply the recommended model to the ${label.toLowerCase()}`}
                  />
                  {body}
                </label>
              ) : (
                <div key={item.agent} className={rowClass} data-testid={`${item.agent}-recommendation`}>
                  <span />
                  {body}
                </div>
              )
            })}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <Button type="button" onClick={() => void apply()} disabled={running || busy || count === 0}>
              {count === 0 ? 'Apply' : `Apply ${count} recommendation${count === 1 ? '' : 's'}`}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} disabled={running}>
              Close
            </Button>
            <span className="text-xs text-muted-foreground">
              {running
                ? 'Checking and saving, one agent at a time.'
                : finished
                  ? 'Done.'
                  : "Each one is checked first. One that fails isn't saved; one whose supplier can't be reached is saved and marked not checked."}
            </span>
          </div>
        </>
      )}

      {view.phase !== 'ready' && (
        <div>
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      )}
    </div>
  )
}
